<script setup lang="ts">
const { t, locale } = useI18n()
const route = useRoute()
const story = await useAsyncStoryblok('components/content/cookies-policy', { version: 'publish', language: locale.value })
const {
  cookiesEnabledIds,
  moduleOptions,
  isConsentGiven,
} = useCookieControl()

onMounted(() => {
  moduleOptions.isAcceptNecessaryButtonEnabled = true
  moduleOptions.isControlButtonEnabled = false
})

// example: react to a cookie being accepted
watch(
  () => cookiesEnabledIds.value,
  (current, previous) => {
    // console.log(route.path)

    if (
      (!previous?.includes('google-analytics')
      && current?.includes('google-analytics'))
    ) {
      // cookie with id `google-analytics` got added
      window.location.reload() // placeholder for your custom change handler
    }
  },
  { deep: true },
)

watchEffect(() => {
  if (route.path === '/questionnaire')
    isConsentGiven.value = true
})
</script>

<template>
  <CookieControl class="!font-sans">
    <template #bar>
      <div class="mb-4 flex items-center gap-x-3">
        <svg class="hidden text-secondary-red-100 sm:flex" xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2c.714 0 1.419.075 2.106.222a.75.75 0 0 1 .374 1.263a2.501 2.501 0 0 0 1.206 4.201a.75.75 0 0 1 .577.811a2.5 2.5 0 0 0 4.36 1.908a.75.75 0 0 1 1.307.409c.047.39.07.787.07 1.186c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2Zm0 1.5a8.5 8.5 0 1 0 8.466 9.265l.019-.257l.007-.157l-.194.088c-.33.139-.68.233-1.043.28l-.274.024l-.231.007a4.001 4.001 0 0 1-3.956-3.402l-.025-.201l-.014-.187l-.145-.06a4.004 4.004 0 0 1-2.338-3.234l-.017-.216l-.005-.2c0-.447.074-.882.214-1.292l.091-.243l.085-.19l-.276-.017L12 3.5ZM15 16a1 1 0 1 1 0 2a1 1 0 0 1 0-2Zm-7-1a1 1 0 1 1 0 2a1 1 0 0 1 0-2Zm4-4a1 1 0 1 1 0 2a1 1 0 0 1 0-2ZM7 8a1 1 0 1 1 0 2a1 1 0 0 1 0-2Z" /></svg>
        <h3 class="text-xl font-bold md:text-2xl">
          {{ t('cookie.title') }}
        </h3>
      </div>
      <p>
        {{ t('cookie.body') }} <a :href="story.content.url.url ? story.content.url.url : ''" class="text-secondary-red-100 font-semibold hover:underline" target="_blank" rel="noopener">{{ t('cookie.policy') }}</a>
      </p>
    </template>
  </CookieControl>
</template>

<style>
.cookieControl__ControlButton {
  @apply hidden;
}

.cookieControl__ModalContentInner {
  @apply !p-7;
}
.cookieControl__ModalContent h2 {
  @apply text-primary font-bold;
}
.cookieControl__Bar {
  @apply md:m-0 m-[15px]
}

.cookieControl__ModalClose {
  @apply !p-2.5;
}

.cookieControl__ModalContent h3, .cookieControl__ModalContent span, .cookieControl__ModalContent label {
  color: #140A4B !important;
}

.cookieControl__BarContainer, .cookieControl__BarContainer p, .cookieControl__BarContainer button, .cookieControl__BarContainer h3 {
  font-family: 'Montserrat', 'Kanit', sans-serif !important;
}

.cookieControl__BarContainer > div {
  margin-right: auto;
}

.cookieControl__BarButtons {
  margin-top: 25px !important;
  width: 100%;
}

.cookieControl__Bar--center p, .cookieControl__Bar--top-left p, .cookieControl__Bar--top-right p, .cookieControl__Bar--bottom-left p, .cookieControl__Bar--bottom-right p {
    max-width: 480px !important;
}

.cookieControl__ModalButtons {
  gap: 15px;
}

.cookieControl__BarButtons button {
  /* width: 100%; */
  /* margin-right: auto; */
  font-size: 16px;
  transition: .2s ease-in-out;
  font-weight: 600;
  border-radius: 6px;
  padding: 10px 20px;
}

.cookieControl__BarButtons button:hover, .cookieControl__ModalButtons button:hover {
  box-shadow: 0 0 1px 3px rgba(237, 36, 52, 0.25) !important;
  box-shadow: 0 0 0 3px -moz-mac-focusring; /* Firefox */
  outline: auto 0 -webkit-focus-ring-color; /* Webkit, Safari */
  transition: .2s ease-in-out;
}

.cookieControl__BarButtons button:focus, .cookieControl__Modal button:focus {
  outline: none  !important;
  box-shadow: none  !important;
}

.cookieControl__ModalContent input:checked+button {
  background-color: #EE2334 !important;
}

.cookieControl__BarButtons button:first-child, .cookieControl__BarButtons button:first-child:hover, .cookieControl__ModalButtons button:not(:last-child), .cookieControl__ModalButtons button:not(:last-child):hover {
  color: #fff;
  background-color: #EE2334 !important;
  border-radius: 6px;
  font-weight: 600;
}

.cookieControl__BarButtons button:nth-child(2), .cookieControl__BarButtons button:nth-child(2):hover, .cookieControl__ModalButtons button:nth-child(2), .cookieControl__ModalButtons button:nth-child(2):hover {
  color: #EE2334;
  border: 1px solid #EE2334 !important;
  background-color: #ffff !important;
  border-radius: 6px;
  font-weight: 600;
}

.cookieControl__BarButtons button:last-child, .cookieControl__BarButtons button:last-child:hover, .cookieControl__ModalContent button:last-child, .cookieControl__ModalContent button:last-child:hover {
  color: #EE2334;
  border: 0 !important;
  background-color: #ffff;
  border-radius: 6px;
  font-weight: 600;
}

/* .cookieControl__ModalContent input:checked:disabled + button {
  background-color: #cbd5e1 !important;
} */

.cookieControl__ModalContent input + button {
  border-radius: 50px !important;
  background-color: #cbd5e1 !important;
}

.cookieControl__ModalContent button, .cookieControl__ModalContent button:hover, .cookieControl__ModalInputWrapper button {
  color: #fff;
  background-color: #EE2334;
  border-radius: 6px;
  font-weight: 600;
}

.cookieControl__Bar.cookieControl__Bar {
  overflow: hidden;
  background-color: #fff !important;
  border-radius: 6px;
  box-shadow: 0 4px 20px 0px rgb(97,88,170, 0.15), 0 2px 12px -7px rgb(97,88,170, 0.15);
}

.cookieControl__Bar::before {
  content: '';
  z-index: 10;
  position: absolute;
  background-color: #EE2334;
  height: 4px;
  width: 100%;
}

.cookieControl__ModalContent h3 {
  @apply !font-bold;
}

.cookieControl__ModalContent label {
  @apply font-semibold;
}

.cookieControl__Bar.cookieControl__Bar h3, .cookieControl__Bar.cookieControl__Bar p {
  font-family: inherit;
  color: #140A4B;
}

.cookieControl__ModalContent {
  overflow-y: auto !important;
  border-radius: 6px;
}

@media screen and (min-width: 376px) {
  .cookieControl__Bar::before {
    content: '';
    z-index: 10;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: #EE2334;
    width: 4px;
    height: 100%;
  }

  .cookieControl__BarButtons {
    flex-direction: row !important;
    justify-content: flex-start !important;
  }
  .cookieControl__BarButtons button {
    margin-top: 0 !important;
    width: auto !important;
    box-shadow: none !important;
    outline: none !important;
  }
  .cookieControl__Bar button + button {
    margin-left: 10px !important;
  }
}
</style>
